import React from "react";
import { AlertCircle, Sparkles } from "lucide-react";

// Localised helper text ("Alebo skús:" / "Or try:" …). Message itself comes
// from backend so it can differ between register and PATCH /auth/me flows.
const TRY_LABEL = {
  sk: "Alebo skús jeden z týchto:",
  en: "Or try one of these:",
  cs: "Nebo zkus jeden z těchto:",
  de: "Oder versuche einen dieser:",
  pl: "Lub spróbuj jednego z tych:",
};

/**
 * Renders a golden alert box with the backend-provided error message and
 * three tappable suggestion pills. Clicking a pill calls onPick(name) so the
 * parent can set the form value and let the user re-submit.
 *
 * Props:
 *   error   — { message: string, suggestions?: string[] } | string | null
 *   lang    — current UI language
 *   onPick  — (suggestion:string) => void
 *   testid  — data-testid prefix (default "name-error")
 */
export default function NameSuggestions({ error, lang = "sk", onPick, testid = "name-error" }) {
  if (!error) return null;
  const msg = typeof error === "string" ? error : error.message || "";
  const suggestions = (typeof error === "object" && Array.isArray(error.suggestions))
    ? error.suggestions.filter(Boolean).slice(0, 3)
    : [];
  const tryLabel = TRY_LABEL[lang] || TRY_LABEL.sk;
  return (
    <div
      data-testid={testid}
      className="mt-2 border border-red-800/60 bg-red-950/25 px-3 py-2.5 text-xs text-red-300 flex flex-col gap-2"
    >
      <div className="flex items-start gap-2">
        <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <span className="leading-relaxed">{msg}</span>
      </div>
      {suggestions.length > 0 && (
        <div className="flex flex-col gap-1.5 pl-6">
          <div className="text-[10px] uppercase tracking-widest text-gold-400/80 font-mono flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" /> {tryLabel}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {suggestions.map((s, i) => (
              <button
                key={i}
                type="button"
                data-testid={`${testid}-suggestion-${i}`}
                onClick={() => onPick?.(s)}
                className="wb-btn-ghost px-2.5 py-1 text-xs !text-gold-300 !border-gold-700/50 hover:!bg-gold-950/40 hover:!text-gold-200 transition"
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
